import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { EventsClient } from '../events-client.js';
import Logger from '../utils/logger.js';
import {
    ACCOUNT_MENU_ACTIONS,
    ACCOUNT_MENU_LABELS,
    createAccountMenuSchema,
    readAccountSchemaButtonLabel,
    readAccountSchemaInputPlaceholder,
} from './schemas/account-menu-schema.js';

const MODULE = 'AccountSubmenu';

const CSS = `
        .mm-account-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
            margin-top: 0.5rem;
        }

        .mm-account-card {
            border: 1px solid #00d9ff;
            border-radius: 10px;
            padding: 0.75rem;
            background: rgba(10, 15, 30, 0.45);
            text-align: left;
        }

        .mm-account-row {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0.35rem;
            margin-bottom: 0.65rem;
        }

        .mm-account-row:last-child {
            margin-bottom: 0;
        }

        .mm-account-label {
            color: #b8e3f0;
            font-size: clamp(0.8rem, 2.2vw, 0.95rem);
            font-weight: 600;
        }

        .mm-account-readonly {
            color: #00ffff;
            font-size: clamp(0.85rem, 2.3vw, 1rem);
            font-weight: 700;
            padding: 0.4rem 0;
            border-bottom: 1px solid rgba(0, 217, 255, 0.3);
        }

        .mm-account-status {
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
            min-height: 1.2em;
            margin-top: 0.35rem;
        }

        .mm-account-status.success {
            color: #00ff9d;
            text-shadow: 0 0 6px rgba(0, 255, 157, 0.5);
        }

        .mm-account-status.error {
            color: #ff4444;
            text-shadow: 0 0 6px rgba(255, 68, 68, 0.5);
        }

        .mm-account-divider {
            height: 1px;
            margin: 0.55rem 0 0.75rem;
            background: linear-gradient(90deg, rgba(0, 217, 255, 0), rgba(0, 217, 255, 0.65), rgba(0, 217, 255, 0));
        }

        .mm-account-hidden-username {
            position: absolute;
            left: -10000px;
            width: 1px;
            height: 1px;
            opacity: 0;
            pointer-events: none;
        }
`;

const MM_ACCOUNT_HTML = `
        <div id="menu-modal-account" class="menu-modal-content">
            <h2>Account</h2>
            <div class="mm-account-section">
                <div class="mm-account-card">
                    <div class="mm-account-row">
                        <label class="mm-account-label">Username</label>
                        <div id="mm-account-username" class="mm-account-readonly">—</div>
                    </div>
                    <div class="mm-account-row">
                        <label id="mm-account-token-label" class="mm-account-label">Token Balance</label>
                        <div id="mm-account-token-balance" class="mm-account-readonly">0</div>
                    </div>
                    <div class="mm-account-row">
                        <label class="mm-account-label" for="mm-account-display-name">Display Name</label>
                        <input id="mm-account-display-name" class="mm-text-input" type="text"
                               placeholder="Display Name" onkeydown="event.stopPropagation()" />
                    </div>
                    <div class="mm-account-row">
                        <label class="mm-account-label" for="mm-account-email">Email</label>
                        <input id="mm-account-email" class="mm-text-input" type="email"
                               placeholder="Email address" onkeydown="event.stopPropagation()" />
                    </div>
                    <div>
                        <button id="mm-account-save-button" class="mm-submit-button">Save Changes</button>
                    </div>
                    <div id="mm-account-profile-status" class="mm-account-status"></div>

                    <div class="mm-account-divider"></div>
                    <form id="mm-account-password-form" onsubmit="return false;" autocomplete="on">
                        <input id="mm-account-password-username" class="mm-account-hidden-username" type="text"
                               autocomplete="username" tabindex="-1" aria-hidden="true" />
                        <div class="mm-account-row">
                            <label class="mm-account-label" for="mm-account-current-password">Current Password</label>
                            <input id="mm-account-current-password" class="mm-text-input" type="password"
                                   placeholder="Current password" autocomplete="current-password" onkeydown="event.stopPropagation()" />
                        </div>
                        <div class="mm-account-row">
                            <label class="mm-account-label" for="mm-account-new-password">New Password</label>
                            <input id="mm-account-new-password" class="mm-text-input" type="password"
                                   placeholder="New password (min 8 chars)" autocomplete="new-password" onkeydown="event.stopPropagation()" />
                        </div>
                        <div class="mm-account-row">
                            <label class="mm-account-label" for="mm-account-confirm-password">Confirm New Password</label>
                            <input id="mm-account-confirm-password" class="mm-text-input" type="password"
                                   placeholder="Confirm new password" autocomplete="new-password" onkeydown="event.stopPropagation()" />
                        </div>
                        <div>
                            <button id="mm-account-password-button" class="mm-submit-button" type="submit" disabled>Change Password</button>
                        </div>
                        <div id="mm-account-password-status" class="mm-account-status"></div>
                    </form>
                </div>
            </div>
        </div>
`;

interface UserProfile {
    id: string;
    username: string;
    display_name: string | null;
    email: string | null;
    token_balance?: number;
    created_at: string;
    updated_at: string;
}

export class AccountSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'AccountSubmenuDH';

    private styleEl: HTMLStyleElement | null = null;

    updateConditionals(): void {
        // Nothing requires periodic refresh on this submenu.
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        this.styleEl = document.createElement('style');
        this.styleEl.innerText = CSS;
        document.head.appendChild(this.styleEl);

        containerDiv.innerHTML = MM_ACCOUNT_HTML;
        const accountSchema = createAccountMenuSchema();
        this.applySchemaLabels(containerDiv, accountSchema);

        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            Logger.error(MODULE, 'EventsClient singleton not available.');
            return;
        }

        const token = eventsClient.getAuthToken();
        if (!token) {
            this.setProfileStatus('Not authenticated.', 'error');
            return;
        }

        this.loadProfile(eventsClient, token);

        const saveBtn = document.getElementById('mm-account-save-button') as HTMLButtonElement | null;
        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                this.saveProfile(eventsClient, token);
            });
        }

        const passwordForm = document.getElementById('mm-account-password-form') as HTMLFormElement | null;
        if (passwordForm) {
            passwordForm.addEventListener('submit', (event) => {
                event.preventDefault();
                this.changePassword(eventsClient, token);
            });
        }

        this.bindPasswordValidation();
    }

    hideContent(): void {
        if (this.styleEl && this.styleEl.parentNode) {
            this.styleEl.parentNode.removeChild(this.styleEl);
            this.styleEl = null;
        }
    }

    private applySchemaLabels(containerDiv: HTMLDivElement, accountSchema: ReturnType<typeof createAccountMenuSchema>): void {
        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = accountSchema.title;
        }

        const usernameLabel = containerDiv.querySelector('label.mm-account-label:not([for])');
        if (usernameLabel) {
            usernameLabel.textContent = ACCOUNT_MENU_LABELS.username;
        }

        const displayNameLabel = containerDiv.querySelector('label[for="mm-account-display-name"]');
        if (displayNameLabel) {
            displayNameLabel.textContent = ACCOUNT_MENU_LABELS.displayName;
        }

        const tokenLabel = document.getElementById('mm-account-token-label');
        if (tokenLabel) {
            tokenLabel.textContent = ACCOUNT_MENU_LABELS.tokenBalance;
        }

        const displayNameInput = document.getElementById('mm-account-display-name') as HTMLInputElement | null;
        if (displayNameInput) {
            displayNameInput.placeholder = readAccountSchemaInputPlaceholder(
                accountSchema,
                'account-display-name-input',
                ACCOUNT_MENU_LABELS.displayNamePlaceholder,
            );
        }

        const emailLabel = containerDiv.querySelector('label[for="mm-account-email"]');
        if (emailLabel) {
            emailLabel.textContent = ACCOUNT_MENU_LABELS.email;
        }

        const emailInput = document.getElementById('mm-account-email') as HTMLInputElement | null;
        if (emailInput) {
            emailInput.placeholder = ACCOUNT_MENU_LABELS.emailPlaceholder;
        }

        const saveBtn = document.getElementById('mm-account-save-button') as HTMLButtonElement | null;
        if (saveBtn) {
            saveBtn.textContent = readAccountSchemaButtonLabel(
                accountSchema,
                ACCOUNT_MENU_ACTIONS.saveProfile,
                ACCOUNT_MENU_LABELS.saveChanges,
            );
        }

        const currentPasswordLabel = containerDiv.querySelector('label[for="mm-account-current-password"]');
        if (currentPasswordLabel) {
            currentPasswordLabel.textContent = ACCOUNT_MENU_LABELS.currentPassword;
        }

        const currentPasswordInput = document.getElementById('mm-account-current-password') as HTMLInputElement | null;
        if (currentPasswordInput) {
            currentPasswordInput.placeholder = ACCOUNT_MENU_LABELS.currentPasswordPlaceholder;
        }

        const newPasswordLabel = containerDiv.querySelector('label[for="mm-account-new-password"]');
        if (newPasswordLabel) {
            newPasswordLabel.textContent = ACCOUNT_MENU_LABELS.newPassword;
        }

        const newPasswordInput = document.getElementById('mm-account-new-password') as HTMLInputElement | null;
        if (newPasswordInput) {
            newPasswordInput.placeholder = ACCOUNT_MENU_LABELS.newPasswordPlaceholder;
        }

        const confirmPasswordLabel = containerDiv.querySelector('label[for="mm-account-confirm-password"]');
        if (confirmPasswordLabel) {
            confirmPasswordLabel.textContent = ACCOUNT_MENU_LABELS.confirmPassword;
        }

        const confirmPasswordInput = document.getElementById('mm-account-confirm-password') as HTMLInputElement | null;
        if (confirmPasswordInput) {
            confirmPasswordInput.placeholder = ACCOUNT_MENU_LABELS.confirmPasswordPlaceholder;
        }

        const passwordBtn = document.getElementById('mm-account-password-button') as HTMLButtonElement | null;
        if (passwordBtn) {
            passwordBtn.textContent = readAccountSchemaButtonLabel(
                accountSchema,
                ACCOUNT_MENU_ACTIONS.changePassword,
                ACCOUNT_MENU_LABELS.changePassword,
            );
        }
    }

    private async loadProfile(eventsClient: EventsClient, token: string): Promise<void> {
        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/users/me`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!resp.ok) {
                Logger.warn(MODULE, `Failed to load profile: ${resp.status}`);
                this.setProfileStatus('Failed to load account information.', 'error');
                return;
            }

            const profile = (await resp.json()) as UserProfile;

            const usernameEl = document.getElementById('mm-account-username');
            const tokenBalanceEl = document.getElementById('mm-account-token-balance');
            const passwordUsernameEl = document.getElementById('mm-account-password-username') as HTMLInputElement | null;
            const displayNameEl = document.getElementById('mm-account-display-name') as HTMLInputElement | null;
            const emailEl = document.getElementById('mm-account-email') as HTMLInputElement | null;

            if (usernameEl) usernameEl.textContent = profile.username;
            if (tokenBalanceEl) tokenBalanceEl.textContent = String(Number(profile.token_balance ?? 0));
            if (passwordUsernameEl) passwordUsernameEl.value = profile.username;
            if (displayNameEl) displayNameEl.value = profile.display_name ?? '';
            if (emailEl) emailEl.value = profile.email ?? '';
        } catch (err) {
            Logger.error(MODULE, 'Error loading profile', err);
            this.setProfileStatus('Error loading account information.', 'error');
        }
    }

    private async saveProfile(eventsClient: EventsClient, token: string): Promise<void> {
        const displayNameEl = document.getElementById('mm-account-display-name') as HTMLInputElement | null;
        const emailEl = document.getElementById('mm-account-email') as HTMLInputElement | null;
        const saveBtn = document.getElementById('mm-account-save-button') as HTMLButtonElement | null;

        if (!displayNameEl || !emailEl) return;

        const display_name = displayNameEl.value.trim() || null;
        const email = emailEl.value.trim() || null;

        if (saveBtn) saveBtn.disabled = true;
        this.setProfileStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/users/me`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ display_name, email }),
            });

            const data = await resp.json();

            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? 'Save failed.';
                this.setProfileStatus(msg, 'error');
            } else {
                const updated = data as UserProfile;
                if (displayNameEl) displayNameEl.value = updated.display_name ?? '';
                if (emailEl) emailEl.value = updated.email ?? '';
                this.setProfileStatus('Changes saved.', 'success');
            }
        } catch (err) {
            Logger.error(MODULE, 'Error saving profile', err);
            this.setProfileStatus('Error saving changes.', 'error');
        } finally {
            if (saveBtn) saveBtn.disabled = false;
        }
    }

    private bindPasswordValidation(): void {
        const currentPasswordEl = document.getElementById('mm-account-current-password') as HTMLInputElement | null;
        const newPasswordEl = document.getElementById('mm-account-new-password') as HTMLInputElement | null;
        const confirmPasswordEl = document.getElementById('mm-account-confirm-password') as HTMLInputElement | null;

        if (!currentPasswordEl || !newPasswordEl || !confirmPasswordEl) return;

        currentPasswordEl.addEventListener('input', () => this.updatePasswordValidationState());
        newPasswordEl.addEventListener('input', () => this.updatePasswordValidationState());
        confirmPasswordEl.addEventListener('input', () => this.updatePasswordValidationState());
        this.updatePasswordValidationState();
    }

    private updatePasswordValidationState(): void {
        const currentPasswordEl = document.getElementById('mm-account-current-password') as HTMLInputElement | null;
        const newPasswordEl = document.getElementById('mm-account-new-password') as HTMLInputElement | null;
        const confirmPasswordEl = document.getElementById('mm-account-confirm-password') as HTMLInputElement | null;

        if (!currentPasswordEl || !newPasswordEl || !confirmPasswordEl) return;

        const currentPassword = currentPasswordEl.value;
        const newPassword = newPasswordEl.value;
        const confirmPassword = confirmPasswordEl.value;

        const hasAllFields = currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;
        const newPasswordLongEnough = newPassword.length >= 8;
        const passwordsMatch = newPassword === confirmPassword;

        this.setPasswordButtonEnabled(hasAllFields && newPasswordLongEnough && passwordsMatch);

        if (newPassword.length === 0 && confirmPassword.length === 0) {
            this.setPasswordStatus('', '');
            return;
        }

        if (!newPasswordLongEnough) {
            this.setPasswordStatus('New password must be at least 8 characters.', 'error');
            return;
        }

        if (!passwordsMatch) {
            this.setPasswordStatus('New password and confirmation do not match.', 'error');
            return;
        }

        this.setPasswordStatus('', '');
    }

    private async changePassword(eventsClient: EventsClient, token: string): Promise<void> {
        const currentPasswordEl = document.getElementById('mm-account-current-password') as HTMLInputElement | null;
        const newPasswordEl = document.getElementById('mm-account-new-password') as HTMLInputElement | null;
        const confirmPasswordEl = document.getElementById('mm-account-confirm-password') as HTMLInputElement | null;
        const passwordBtn = document.getElementById('mm-account-password-button') as HTMLButtonElement | null;

        if (!currentPasswordEl || !newPasswordEl || !confirmPasswordEl) return;

        const currentPassword = currentPasswordEl.value;
        const newPassword = newPasswordEl.value;
        const confirmPassword = confirmPasswordEl.value;

        if (newPassword !== confirmPassword) {
            this.setPasswordStatus('New password and confirmation do not match.', 'error');
            return;
        }

        if (newPassword.length < 8) {
            this.setPasswordStatus('New password must be at least 8 characters.', 'error');
            return;
        }

        if (passwordBtn) passwordBtn.disabled = true;
        this.setPasswordStatus('', '');
        let changedSuccessfully = false;

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/users/me/password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            const data = await resp.json();

            if (!resp.ok) {
                const errorText = ((data as { error?: string }).error ?? '').toLowerCase();
                if (resp.status === 401 && errorText.includes('current password')) {
                    this.setPasswordStatus('Invalid Current Password', 'error');
                } else {
                    const msg = (data as { error?: string }).error ?? 'Password update failed.';
                    this.setPasswordStatus(msg, 'error');
                }
                return;
            }

            currentPasswordEl.value = '';
            newPasswordEl.value = '';
            confirmPasswordEl.value = '';
            changedSuccessfully = true;
            this.setPasswordStatus('Password change successful.', 'success');
        } catch (err) {
            Logger.error(MODULE, 'Error changing password', err);
            this.setPasswordStatus('Error updating password.', 'error');
        } finally {
            if (changedSuccessfully) {
                this.setPasswordButtonEnabled(false);
            } else {
                this.setPasswordButtonEnabled(this.isPasswordFormSubmittable());
            }
        }
    }

    private isPasswordFormSubmittable(): boolean {
        const currentPasswordEl = document.getElementById('mm-account-current-password') as HTMLInputElement | null;
        const newPasswordEl = document.getElementById('mm-account-new-password') as HTMLInputElement | null;
        const confirmPasswordEl = document.getElementById('mm-account-confirm-password') as HTMLInputElement | null;

        if (!currentPasswordEl || !newPasswordEl || !confirmPasswordEl) return false;

        const currentPassword = currentPasswordEl.value;
        const newPassword = newPasswordEl.value;
        const confirmPassword = confirmPasswordEl.value;

        return currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;
    }

    private setProfileStatus(message: string, type: '' | 'success' | 'error'): void {
        this.setStatus('mm-account-profile-status', message, type);
    }

    private setPasswordStatus(message: string, type: '' | 'success' | 'error'): void {
        this.setStatus('mm-account-password-status', message, type);
    }

    private setPasswordButtonEnabled(enabled: boolean): void {
        const passwordBtn = document.getElementById('mm-account-password-button') as HTMLButtonElement | null;
        if (!passwordBtn) return;
        passwordBtn.disabled = !enabled;
    }

    private setStatus(statusElementId: string, message: string, type: '' | 'success' | 'error'): void {
        const statusEl = document.getElementById(statusElementId);
        if (!statusEl) return;
        statusEl.textContent = message;
        statusEl.className = `mm-account-status${type ? ` ${type}` : ''}`;
    }
}
